/**
 * @file OrderRefundModal.tsx
 *
 * Refund-order dialog: shows the full refund amount and offers an optional reason and
 * a restock toggle, on Gravity's {@link StandardDialog}. The admin issues full refunds
 * only; the parent supplies the confirm handler (which calls the store's `refundOrder`)
 * and the saving / error state.
 */

import { useId, useState } from 'react';
import { StandardDialog } from '@group.one/react-gravity';
import { useIntl } from 'react-intl';
import { adminMessages } from '../i18n/messages';
import type { ShopOrder } from '../api/shop-types';

interface OrderRefundModalProps {
  order: ShopOrder;
  currencySymbol: string;
  saving: boolean;
  /** Optional error shown in-dialog when the refund fails. */
  errorMessage?: string;
  onConfirm: (opts: { reason: string; restock: boolean }) => void;
  onClose: () => void;
}

/**
 * Renders the refund-order dialog.
 *
 * @param props - The order, the shop currency symbol, the loading/error state, and
 *   the confirm/close handlers.
 *
 * @returns The dialog element.
 */
export function OrderRefundModal({
  order,
  currencySymbol,
  saving,
  errorMessage,
  onConfirm,
  onClose,
}: OrderRefundModalProps) {
  const intl = useIntl();
  const reasonId = useId();
  const restockId = useId();
  const [reason, setReason] = useState('');
  const [restock, setRestock] = useState(false);

  const handleDismiss = (): void => {
    if (saving) {
      return;
    }
    onClose();
  };

  const amount = `${order.currency_symbol || currencySymbol}${order.total}`;

  return (
    <StandardDialog
      isOpen
      title={intl.formatMessage(adminMessages.ordersRefundTitle)}
      onClose={handleDismiss}
      onClickAway={handleDismiss}
      onEscape={handleDismiss}
      footer={
        <div className="gv-button-group">
          <button
            className="gv-button gv-button-cancel"
            type="button"
            data-testid="order-refund-dismiss"
            onClick={handleDismiss}
            disabled={saving}
          >
            {intl.formatMessage(adminMessages.ordersCancel)}
          </button>
          <button
            className="gv-button gv-button-primary"
            type="button"
            data-testid="order-refund-confirm"
            onClick={() => onConfirm({ reason, restock })}
            disabled={saving}
          >
            {saving
              ? intl.formatMessage(adminMessages.ordersRefundLoading)
              : intl.formatMessage(adminMessages.ordersRefundConfirm)}
          </button>
        </div>
      }
    >
      <div
        className="gv-flex gv-flex-col gv-gap-md gv-w-full"
        data-testid="order-refund-modal"
      >
        <div className="gv-flex gv-flex-col">
          <span className="gv-text-sm gv-text-bold">
            {intl.formatMessage(adminMessages.ordersRefundAmountLabel)}
          </span>
          <span className="gv-text-sm" data-testid="order-refund-amount">
            {amount}
          </span>
        </div>

        <div className="gv-form-option gv-w-full">
          <label className="gv-label" htmlFor={reasonId}>
            {intl.formatMessage(adminMessages.ordersRefundReasonLabel)}
          </label>
          <textarea
            id={reasonId}
            className="gv-input gv-input-textarea gv-w-full"
            rows={3}
            data-testid="order-refund-reason"
            value={reason}
            placeholder={intl.formatMessage(adminMessages.ordersRefundReasonPlaceholder)}
            onChange={(event) => setReason(event.target.value)}
          />
        </div>

        <label className="gv-option" htmlFor={restockId}>
          <input
            id={restockId}
            type="checkbox"
            className="gv-checkbox"
            data-testid="order-refund-restock"
            checked={restock}
            onChange={(event) => setRestock(event.target.checked)}
          />
          <span className="gv-item-text">
            {intl.formatMessage(adminMessages.ordersRefundRestockLabel)}
          </span>
        </label>

        {errorMessage && (
          <p
            className="gv-text-sm gv-state-critical"
            role="alert"
            data-testid="order-refund-error"
            style={{ marginBottom: 0 }}
          >
            {errorMessage}
          </p>
        )}
      </div>
    </StandardDialog>
  );
}
